import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import ShieldCheckIcon from '@heroicons/react/outline/ShieldCheckIcon';
import UserRemoveIcon from '@heroicons/react/outline/UserRemoveIcon';
import InfiniteScroll from 'react-infinite-scroller';
import { hasScopesPermission } from '@tribeplatform/gql-client';
import { SpaceRoleType, } from '@tribeplatform/gql-client/types';
import { useRemoveSpaceMember, useSpaceMembers, useSpaceRoles, useUpdateMemberSpaceRole, } from '@tribeplatform/react-sdk/hooks';
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils';
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar';
import { Badge } from '@tribeplatform/react-ui-kit/Badge';
import { Button } from '@tribeplatform/react-ui-kit/Button';
import { confirm } from '@tribeplatform/react-ui-kit/Dialog';
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown';
import { DotsVerticalIcon } from '@tribeplatform/react-ui-kit/icons';
import { Container } from '@tribeplatform/react-ui-kit/Layout';
import { Link } from '@tribeplatform/react-ui-kit/Link';
import { Modal } from '@tribeplatform/react-ui-kit/Modal';
import { T, useI18n } from '../i18n';
export const SpaceMembersModal = ({ space, open, onClose }) => {
    const { $t } = useI18n();
    const spaceId = space === null || space === void 0 ? void 0 : space.id;
    const { data, fetchNextPage, hasNextPage, isFetched, isLoading, isFetchingNextPage, } = useSpaceMembers({
        fields: {
            member: {
                profilePicture: 'basic',
                banner: 'basic',
                role: 'basic',
                badges: 'default',
            },
            role: 'basic',
        },
        variables: { limit: 30, spaceId },
        useInfiniteQueryOptions: {
            refetchOnMount: 'always',
        },
    });
    const { data: roles } = useSpaceRoles({
        variables: {
            spaceId,
        },
    });
    const { mutateAsync: removeSpaceMember } = useRemoveSpaceMember();
    const { mutateAsync: updateMemberSpaceRole } = useUpdateMemberSpaceRole();
    const [canUpdateMemberSpaceRole, canRemoveSpaceMembers] = hasScopesPermission(space, ['updateMemberSpaceRole', 'removeSpaceMembers']);
    const adminRole = roles === null || roles === void 0 ? void 0 : roles.find(role => role.type === SpaceRoleType.admin);
    const memberRole = roles === null || roles === void 0 ? void 0 : roles.find(role => role.type === SpaceRoleType.member);
    const getOtherMemberRole = (spaceMember) => { var _a; return ((_a = spaceMember === null || spaceMember === void 0 ? void 0 : spaceMember.role) === null || _a === void 0 ? void 0 : _a.id) === (adminRole === null || adminRole === void 0 ? void 0 : adminRole.id) ? memberRole : adminRole; };
    const getMemberCardActions = (spaceMember) => {
        var _a, _b;
        if (!(canUpdateMemberSpaceRole || canRemoveSpaceMembers)) {
            return null;
        }
        const { member } = spaceMember;
        const otherRole = getOtherMemberRole(spaceMember);
        return (_jsxs(_Fragment, { children: [canRemoveSpaceMembers && (_jsx(Dropdown.Item, { leadingIcon: _jsx(UserRemoveIcon, {}, void 0), onClick: async () => {
                        const confirmed = await confirm({
                            title: $t({
                                id: 'Space.Members.Remove.Confirmation',
                                defaultMessage: 'Are you sure you want to remove {member_name} ({member_username}) from this {SPACE}?',
                            }, {
                                member_name: member === null || member === void 0 ? void 0 : member.name,
                                member_username: member === null || member === void 0 ? void 0 : member.username,
                            }),
                            proceedLabel: $t({
                                defaultMessage: 'Remove',
                                id: 'Generics.Remove',
                            }),
                            danger: true,
                        });
                        if (!confirmed) {
                            return;
                        }
                        await removeSpaceMember({
                            spaceId,
                            memberIds: [member === null || member === void 0 ? void 0 : member.id],
                        });
                    }, children: _jsx(T, { id: "Generics.RemoveMember", defaultMessage: "Remove {MEMBER}" }, void 0) }, void 0)), canUpdateMemberSpaceRole && (_jsx(Dropdown.Item, { leadingIcon: _jsx(ShieldCheckIcon, {}, void 0), onClick: async () => {
                        var _a;
                        const confirmed = await confirm({
                            title: $t({
                                id: 'Space.Members.ChangeRole.Confirmation',
                                defaultMessage: 'Are you sure you want to change role of {member_name} ({member_username}) to {role_name, select, admin {space admin} member {space member} other {space {role_name}}}?',
                            }, {
                                member_name: member === null || member === void 0 ? void 0 : member.name,
                                member_username: member === null || member === void 0 ? void 0 : member.username,
                                role_name: (_a = otherRole === null || otherRole === void 0 ? void 0 : otherRole.name) === null || _a === void 0 ? void 0 : _a.toLowerCase(),
                            }),
                            proceedLabel: $t({
                                id: 'Space.Members.ChangeRole.Proceed',
                                defaultMessage: 'Change role',
                            }),
                        });
                        if (!confirmed) {
                            return;
                        }
                        await updateMemberSpaceRole({
                            memberId: member === null || member === void 0 ? void 0 : member.id,
                            spaceId,
                            input: {
                                roleId: otherRole === null || otherRole === void 0 ? void 0 : otherRole.id,
                            },
                        });
                    }, children: _jsx(T, { defaultMessage: "Change role to {role_name, select, admin {space admin} member {space member} other {space {role_name}}}", id: "Space.Members.ChangeRole", values: {
                            role_name: (_b = (_a = getOtherMemberRole(spaceMember)) === null || _a === void 0 ? void 0 : _a.name) === null || _b === void 0 ? void 0 : _b.toLowerCase(),
                        } }, void 0) }, void 0))] }, void 0));
    };
    const { nodes: members = [] } = simplifyPaginatedResult(data);
    return (_jsxs(Modal, { open: open, onClose: onClose, children: [_jsx(Modal.Header, { title: $t({
                    defaultMessage: 'Space members',
                    id: 'Space.Members',
                }) }, void 0), _jsx(Modal.Content, { children: _jsx(InfiniteScroll, { pageStart: 0, loadMore: fetchNextPage, hasMore: hasNextPage || false, threshold: 800, children: isFetched && !(members === null || members === void 0 ? void 0 : members.length) && !isLoading ? (_jsx("div", { children: _jsx(T, { defaultMessage: "No members in this space.", id: "Space.MemberSettings.Empty" }, void 0) }, void 0)) : (_jsx("div", { className: "sm:max-h-120 overflow-auto -mx-2", children: _jsxs(Container, { size: "full", spacing: "none", children: [members
                                    .filter(spaceMember => spaceMember === null || spaceMember === void 0 ? void 0 : spaceMember.member)
                                    .map(spaceMember => {
                                    var _a, _b, _c;
                                    const { member, role } = spaceMember;
                                    return (_jsxs(Link, { target: "_blank", href: member === null || member === void 0 ? void 0 : member.relativeUrl, className: "flex space-s-3 items-center p-2 rounded-md hover:bg-surface-hovered", variant: "inherit", children: [_jsx(Avatar, { size: "sm", src: (_b = (_a = member === null || member === void 0 ? void 0 : member.profilePicture) === null || _a === void 0 ? void 0 : _a.urls) === null || _b === void 0 ? void 0 : _b.thumb }, void 0), _jsxs("div", { className: "flex-grow flex space-s-2", children: [_jsx("span", { children: member === null || member === void 0 ? void 0 : member.name }, void 0), (role === null || role === void 0 ? void 0 : role.type) === SpaceRoleType.admin && (_jsx(Badge, { variant: "secondary", size: "sm", children: _jsx(T, { defaultMessage: "Space admin", id: "Space.MemberSettings.SpaceAdmin" }, void 0) }, void 0))] }, void 0), _jsx("div", { onClick: e => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                }, children: _jsxs(Dropdown, { placement: "bottom-end", children: [_jsx(Dropdown.ButtonMinimal, { as: "div", children: _jsx(Button, { variant: "basic", leadingIcon: _jsx(DotsVerticalIcon, {}, void 0), size: "sm", rounded: true, className: "bg-transparent" }, void 0) }, void 0), _jsx(Dropdown.Items, { children: getMemberCardActions(spaceMember) }, void 0)] }, void 0) }, void 0)] }, (_c = spaceMember === null || spaceMember === void 0 ? void 0 : spaceMember.member) === null || _c === void 0 ? void 0 : _c.id));
                                }), !isFetched || isLoading || isFetchingNextPage ? null : null] }, void 0) }, void 0)) }, void 0) }, void 0)] }, void 0));
};
//# sourceMappingURL=SpaceMembersModal.js.map